import React from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Shield, Users as UsersIcon } from "lucide-react";

const STAFF_ROLES_ORDERED = [
    ["superadmin", "Super Admin"],
    ["admin", "Admin"],
    ["sales", "Vânzări"],
    ["installer", "Instalator"],
    ["collaborator", "Colaborator"],
];

const STAFF_ROLES = new Set(STAFF_ROLES_ORDERED.map(([k]) => k));

const isStaff = (u) => {
    const roles = (u.roles && u.roles.length ? u.roles : [u.role || "client"]);
    return roles.some(r => STAFF_ROLES.has(r));
};

export default function AdminUsers() {
    const [items, setItems] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(null);

    const load = React.useCallback(() => api.get("/admin/users").then(r => setItems(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const openNew = () => {
        setEditing({ name: "", email: "", phone: "", password: "", roles: ["admin"], custom_discount_percent: 0, active: true });
        setOpen(true);
    };
    const openEdit = (u) => {
        setEditing({ ...u, roles: u.roles?.length ? u.roles : [u.role || "client"], password: "" });
        setOpen(true);
    };

    const save = async () => {
        try {
            const payload = { ...editing, role: editing.roles?.[0] || "admin" };
            if (!payload.password) delete payload.password;
            if (editing.user_id) await api.put(`/admin/users/${editing.user_id}`, payload);
            else await api.post("/admin/users", payload);
            toast.success("Salvat"); setOpen(false); load();
        } catch (e) {
            toast.error("Eroare: " + (e.response?.data?.detail || e.message));
        }
    };

    const del = async (id) => {
        if (!window.confirm("Sigur ștergi?")) return;
        await api.delete(`/admin/users/${id}`); toast.success("Șters"); load();
    };

    const toggleRole = (r) => setEditing(u => {
        const roles = u.roles || [];
        return { ...u, roles: roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r] };
    });

    const staff = items.filter(isStaff);

    return (
        <div data-testid="admin-users">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline mb-1">Administrare</div>
                    <h1 className="font-heading text-3xl">Utilizatori — Staff</h1>
                    <p className="text-xs text-muted-foreground mt-1">Admini, vânzări, instalatori, colaboratori. Clienții sunt administrați în <Link to="/admin/clients" className="text-primary hover:underline">Clienți</Link>.</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/admin/clients" className="btn-outline flex items-center gap-2" data-testid="users-goto-clients">
                        <UsersIcon className="h-4 w-4" /> Clienți
                    </Link>
                    <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="users-add-btn">
                        <Plus className="h-4 w-4" /> Adaugă membru staff
                    </button>
                </div>
            </div>

            <div className="card-soft overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted"><tr>
                        {["Nume", "Email", "Telefon", "Roluri", "Activ", ""].map(h => <th key={h} className="text-left px-3 py-3 text-xs uppercase text-muted-foreground">{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {staff.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">Niciun membru staff încă. Adaugă-l primul.</td></tr>}
                        {staff.map((u, i) => (
                            <tr key={u.user_id} className="border-t border-border" data-testid={`user-row-${i}`}>
                                <td className="px-3 py-2 flex items-center gap-2">
                                    <Shield className="h-3.5 w-3.5 text-primary" />
                                    {u.name}
                                </td>
                                <td className="px-3 py-2 text-xs">{u.email}</td>
                                <td className="px-3 py-2 text-xs">{u.phone || "—"}</td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                        {(u.roles?.length ? u.roles : [u.role]).filter(r => STAFF_ROLES.has(r)).map(r => (
                                            <span key={r} className="chip-primary text-[10px]">{r}</span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-3 py-2">{u.active ? "✓" : "—"}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => openEdit(u)} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(u.user_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-xl">{editing.user_id ? "Editează staff" : "Membru staff nou"}</h2>
                            <button onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
                        </div>
                        <input placeholder="Nume" value={editing.name || ""} onChange={(e) => setEditing(u => ({ ...u, name: e.target.value }))} className="input-soft" />
                        <input placeholder="Email" value={editing.email || ""} onChange={(e) => setEditing(u => ({ ...u, email: e.target.value }))} className="input-soft" />
                        <input placeholder="Telefon" value={editing.phone || ""} onChange={(e) => setEditing(u => ({ ...u, phone: e.target.value }))} className="input-soft" />
                        <input type="password" placeholder={editing.user_id ? "Parolă nouă (opțional)" : "Parolă"} value={editing.password || ""} onChange={(e) => setEditing(u => ({ ...u, password: e.target.value }))} className="input-soft" />
                        <div>
                            <label className="text-xs uppercase text-muted-foreground block mb-2">Roluri (poți selecta mai multe)</label>
                            <div className="flex flex-wrap gap-2">
                                {STAFF_ROLES_ORDERED.map(([k, l]) => (
                                    <button type="button" key={k} onClick={() => toggleRole(k)}
                                            className={`chip ${editing.roles?.includes(k) ? "chip-primary" : "bg-muted text-muted-foreground"} cursor-pointer`}>
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={editing.active !== false}
                                   onChange={(e) => setEditing(u => ({ ...u, active: e.target.checked }))} />
                            Activ
                        </label>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setOpen(false)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary" data-testid="user-save">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
